import { FC, useEffect, useMemo, useState } from 'react';
import { Delete, Done, PlayArrow } from '@mui/icons-material';
import { Paper, IconButton, PaperProps } from '@mui/material';
import { getFileName, getImagePreviewUrl, getVideoThumbnailFromFileOrUrl, isVideoFile } from '@modules/Tributes/helpers/media';


interface ImagePreviewProps {
  file: Tributes.MediaFile;
  onRemove?: (file: Tributes.MediaFile) => void;
  readonly?: boolean;
  onView?: () => void;
  isSelected?: boolean;
  slotProps?: {
    paper?: PaperProps;
  };
}


const ImagePreview: FC<ImagePreviewProps> = (props) => {
  const { file, onRemove, readonly, slotProps, onView, isSelected } = props;

  const [preview, setPreview] = useState<string>(
    (file instanceof File && 'preview' in file && file.preview) ? file.preview! : (typeof file === 'object' && 'file' in file ? file.file : ''),
  );

  const video = useMemo(() => isVideoFile(file), [file]);

  useEffect(() => {
    let url: string | undefined;
    let revoked = false;

    const updatePreview = async () => {
      if (file instanceof File) {
        if (isVideoFile(file)) {
          try {
            const thumb = await getVideoThumbnailFromFileOrUrl(file);
            setPreview(thumb);
          } catch (error) {
            console.error('Error generating video thumbnail:', error);
            url = URL.createObjectURL(file);
            setPreview(url);
          }
        } else {
          url = await getImagePreviewUrl(file);
          setPreview(url);
        }
      } else {
        if (isVideoFile(file)) {
          try {
            const thumb = await getVideoThumbnailFromFileOrUrl(file.file);
            setPreview(thumb);
          } catch (error) {
            console.error('Error generating video thumbnail:', error);
            setPreview(file.file);
          }
        } else {
          setPreview(file.file);
        }
      }
    };
    updatePreview();

    return () => {
      if (url && !revoked) {
        URL.revokeObjectURL(url);
        revoked = true;
      }
    };
  }, [file]);

  return (
    <Paper
      elevation={1}
      onClick={onView}
      {...slotProps?.paper}
      sx={{
        position: 'relative',
        width: isSelected ? 124 : 120,
        height: isSelected ? 124 : 120,
        margin: isSelected ? 0 : '2px',
        transition: 'all 0.1s ease',
        overflow: 'hidden',
        borderRadius: '16px',
        cursor: onView ? 'pointer' : 'default',
        ...(slotProps?.paper?.sx || {}),
        ...(isSelected ? {
          border: theme => `2px solid ${theme.palette.primary.main}`,
        } : {}),
      }}
    >
      <img
        src={preview || undefined}
        alt={getFileName(file)}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {!readonly && (
        <IconButton
          size="small"
          onClick={e => {e.stopPropagation(); onRemove?.(file);}}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      )}
      {isSelected && (
        <Done
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'primary.main',
          }}
        />
      )}
      {video && (
        <IconButton
          onClick={e => { e.stopPropagation(); onView?.(); }}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <PlayArrow fontSize="small" />
        </IconButton>
      )}
    </Paper>
  );
};

export default ImagePreview;
